import React, { useState, useMemo, useCallback } from 'react';
import {
 Plus,
 Edit,
 Trash2,
 Gift,
 Loader2,
 RefreshCw,
 Copy,
 Share2,
 ListChecks,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToastContext } from '../../contexts/ToastContext';
import { vendorDiscountCodeApi } from '../../services/discountCodeService';
import { useVendorProfile } from '../../services/vendorProfileService';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import CustomSelect from '../../components/shared/CustomSelect';
import DatePicker from '../../components/client/DatePicker';
import DataTable from '../../components/shared/DataTable';
import ModalShell from '../../components/shared/ModalShell';
import { VendorPageHeader, VendorGuideCallout, VendorEmptyState } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const PERCENTAGE = 'percentage';
const FIXED = 'fixed';

const defaultForm = {
 code: '',
 discount_type: PERCENTAGE,
 discount_value: '',
 min_booking_amount: '',
 max_uses: '',
 valid_from: '',
 valid_until: '',
 description: '',
 is_active: true,
 published_to_profile: false,
};

/**
 * Get valid code prefixes from business name: first 2, 3, and 4 letters (letters only from start).
 */
const getBusinessNamePrefixes = (businessName) => {
 if (!businessName || typeof businessName !== 'string') return [];
 const letters = businessName.replace(/^[^a-zA-Z]*/, '').match(/^[a-zA-Z]+/);
 const str = (letters ? letters[0] : '').toUpperCase();
 const prefixes = [];
 if (str.length >= 2) prefixes.push(str.slice(0, 2));
 if (str.length >= 3) prefixes.push(str.slice(0, 3));
 if (str.length >= 4) prefixes.push(str.slice(0, 4));
 return [...new Set(prefixes)];
};

/** @returns {'expired'|'not_started'|'in_range'} */
const getDiscountDateValidity = (row) => {
 const now = new Date();
 if (row.valid_until) {
 const u = new Date(row.valid_until);
 const endOfDay = new Date(u.getFullYear(), u.getMonth(), u.getDate(), 23, 59, 59, 999);
 if (now > endOfDay) return 'expired';
 }
 if (row.valid_from) {
 const f = new Date(row.valid_from);
 const startOfFrom = new Date(f.getFullYear(), f.getMonth(), f.getDate());
 const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
 if (startOfToday < startOfFrom) return 'not_started';
 }
 return 'in_range';
};

const formatDiscountDate = (v) => {
 if (!v) return '—';
 const d = new Date(v);
 return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDiscountValue = (row) => {
 if (row.discount_type === PERCENTAGE) {
 return `${Number(row.discount_value)}% off`;
 }
 return `$${Number(row.discount_value).toFixed(2)} off`;
};

const VendorDiscountCodes = () => {
 const { success, error: showError } = useToastContext();
 const queryClient = useQueryClient();
 const [modalOpen, setModalOpen] = useState(false);
 const [editingId, setEditingId] = useState(null);
 const [form, setForm] = useState(defaultForm);
 const [activeFilter, setActiveFilter] = useState('all'); // all | active | inactive

 const { data: profileData } = useVendorProfile();
 const businessName = profileData?.data?.business_name || '';
 const validPrefixes = getBusinessNamePrefixes(businessName);

 const { data, isLoading, error } = useQuery({
 queryKey: ['vendorDiscountCodes', activeFilter],
 queryFn: () =>
 vendorDiscountCodeApi.list({
 is_active: activeFilter === 'all' ? undefined : activeFilter === 'active',
 per_page: 50,
 }),
 });

 const createMutation = useMutation({
 mutationFn: (payload) => vendorDiscountCodeApi.create(payload),
 onSuccess: () => {
 success('Discount code created.');
 queryClient.invalidateQueries({ queryKey: ['vendorDiscountCodes'] });
 queryClient.invalidateQueries({ queryKey: ['published-vendor-discount-codes'] });
 closeModal();
 },
 onError: (e) => {
 showError(e?.response?.data?.message || 'Failed to create discount code.');
 },
 });

 const updateMutation = useMutation({
 mutationFn: ({ id, data }) => vendorDiscountCodeApi.update(id, data),
 onSuccess: () => {
 success('Discount code updated.');
 queryClient.invalidateQueries({ queryKey: ['vendorDiscountCodes'] });
 queryClient.invalidateQueries({ queryKey: ['published-vendor-discount-codes'] });
 closeModal();
 },
 onError: (e) => {
 showError(e?.response?.data?.message || 'Failed to update discount code.');
 },
 });

 const deleteMutation = useMutation({
 mutationFn: (id) => vendorDiscountCodeApi.delete(id),
 onSuccess: () => {
 success('Discount code deleted.');
 queryClient.invalidateQueries({ queryKey: ['vendorDiscountCodes'] });
 queryClient.invalidateQueries({ queryKey: ['published-vendor-discount-codes'] });
 },
 onError: (e) => {
 showError(e?.response?.data?.message || 'Failed to delete.');
 },
 });

 const { mutate: togglePublishOnProfile, isPending: isTogglingPublish, variables: togglePublishVars } =
 useMutation({
 mutationFn: ({ id, published_to_profile }) =>
 vendorDiscountCodeApi.update(id, { published_to_profile }),
 onSuccess: (_, variables) => {
 success(
 variables.published_to_profile
 ? 'Code is now shown on your public vendor page.'
 : 'Code removed from your public vendor page.'
 );
 queryClient.invalidateQueries({ queryKey: ['vendorDiscountCodes'] });
 queryClient.invalidateQueries({ queryKey: ['published-vendor-discount-codes'] });
 },
 onError: (e) => {
 showError(e?.response?.data?.message || 'Could not update profile visibility.');
 },
 });

 const codes = data?.data?.data ?? data?.data ?? [];

 const copyDiscountCode = useCallback(
 async (row) => {
 const code = row?.code;
 if (!code) return;
 try {
 await navigator.clipboard.writeText(code);
 success('Code copied to clipboard.');
 } catch {
 showError('Could not copy. Try again or copy the code manually.');
 }
 },
 [success, showError]
 );

 const shareDiscountCode = useCallback(
 async (row) => {
 const code = row?.code;
 if (!code) return;
 const lines = [
 `Use discount code ${code}${businessName ? ` at ${businessName}` : ''}.`,
 formatDiscountValue(row),
 ];
 if (row.valid_from || row.valid_until) {
 lines.push(
 `Valid ${formatDiscountDate(row.valid_from)} – ${formatDiscountDate(row.valid_until)}`
 );
 }
 const text = lines.join('\n');
 if (typeof navigator !== 'undefined' && navigator.share) {
 try {
 await navigator.share({ title: 'Discount code', text });
 return;
 } catch (e) {
 if (e?.name === 'AbortError') return;
 }
 }
 await copyDiscountCode(row);
 },
 [businessName, copyDiscountCode]
 );

 const openCreate = () => {
 setEditingId(null);
 setForm(defaultForm);
 setModalOpen(true);
 };

 const openEdit = (row) => {
 setEditingId(row.id);
 setForm({
 code: row.code ?? '',
 discount_type: row.discount_type ?? PERCENTAGE,
 discount_value: row.discount_value != null ? String(row.discount_value) : '',
 min_booking_amount: row.min_booking_amount != null ? String(row.min_booking_amount) : '',
 max_uses: row.max_uses != null ? String(row.max_uses) : '',
 valid_from: row.valid_from ? row.valid_from.slice(0, 10) : '',
 valid_until: row.valid_until ? row.valid_until.slice(0, 10) : '',
 description: row.description ?? '',
 is_active: row.is_active ?? true,
 published_to_profile: row.published_to_profile ?? false,
 });
 setModalOpen(true);
 };

 const closeModal = () => {
 setModalOpen(false);
 setEditingId(null);
 setForm(defaultForm);
 };

 const handleSubmit = (e) => {
 e.preventDefault();
 const code = form.code.trim().toUpperCase();
 if (!editingId && validPrefixes.length > 0) {
 const startsWithValid = validPrefixes.some((prefix) => code.startsWith(prefix));
 if (!startsWithValid) {
 showError(
 `Code must start with 2–4 letters from your business name. Valid prefixes: ${validPrefixes.join(', ')}.`
 );
 return;
 }
 }
 const payload = {
 code,
 discount_type: form.discount_type,
 discount_value: parseFloat(form.discount_value) || 0,
 min_booking_amount: form.min_booking_amount ? parseFloat(form.min_booking_amount) : null,
 max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
 valid_from: form.valid_from || null,
 valid_until: form.valid_until || null,
 description: form.description.trim() || null,
 is_active: form.is_active,
 published_to_profile: form.published_to_profile,
 };
 if (editingId) {
 updateMutation.mutate({ id: editingId, data: payload });
 } else {
 createMutation.mutate(payload);
 }
 };

 const handleDiscountSearch = useCallback((dataArray, searchTerm) => {
 const t = searchTerm.toLowerCase().trim();
 if (!t) return dataArray;
 return dataArray.filter((row) => {
 const code = String(row.code || '').toLowerCase();
 const discount = formatDiscountValue(row).toLowerCase();
 const minAmt = row.min_booking_amount != null ? String(row.min_booking_amount) : '';
 const uses = `${row.used_count ?? 0}${row.max_uses != null ? ` / ${row.max_uses}` : ''}`;
 const valid = `${formatDiscountDate(row.valid_from)} ${formatDiscountDate(row.valid_until)}`.toLowerCase();
 return (
 code.includes(t) ||
 discount.includes(t) ||
 minAmt.includes(t) ||
 uses.includes(t) ||
 valid.includes(t)
 );
 });
 }, []);

 const columns = useMemo(
 () => [
 {
 key: 'code',
 label: 'Code',
 sortable: true,
 width: 'w-52',
 render: (row) => (
 <div className="flex items-center gap-2 min-w-0">
 <span className="font-mono font-medium text-gray-900 truncate">{row.code}</span>
 <div className="flex items-center shrink-0 gap-0.5">
 <button
 type="button"
 onClick={(e) => {
 e.stopPropagation();
 copyDiscountCode(row);
 }}
 className="p-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100"
 title="Copy code"
 aria-label="Copy code"
 >
 <Copy className="w-4 h-4" />
 </button>
 <button
 type="button"
 onClick={(e) => {
 e.stopPropagation();
 shareDiscountCode(row);
 }}
 className="p-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100"
 title="Share code"
 aria-label="Share code"
 >
 <Share2 className="w-4 h-4" />
 </button>
 </div>
 </div>
 ),
 },
 {
 key: 'discount_value',
 label: 'Discount',
 sortable: true,
 render: (row) => <span className="text-gray-700">{formatDiscountValue(row)}</span>,
 },
 {
 key: 'min_booking_amount',
 label: 'Min. amount',
 sortable: true,
 render: (row) => (
 <span className="text-gray-600">
 {row.min_booking_amount != null ? `$${Number(row.min_booking_amount).toFixed(2)}` : '—'}
 </span>
 ),
 },
 {
 key: 'used_count',
 label: 'Uses',
 sortable: true,
 render: (row) => (
 <span className="text-gray-600">
 {row.used_count ?? 0}
 {row.max_uses != null ? ` / ${row.max_uses}` : ''}
 </span>
 ),
 },
 {
 key: 'valid_until',
 label: 'Valid',
 sortable: true,
 render: (row) => {
 const validity = getDiscountDateValidity(row);
 return (
 <div>
 <span className={validity === 'expired' ? 'text-amber-800' : 'text-gray-600'}>
 {formatDiscountDate(row.valid_from)} – {formatDiscountDate(row.valid_until)}
 </span>
 {validity === 'expired' && (
 <span className="block text-xs text-amber-600 mt-0.5">Validity period ended</span>
 )}
 {validity === 'not_started' && (
 <span className="block text-xs text-yellow-700 mt-0.5">Not yet active</span>
 )}
 </div>
 );
 },
 },
 {
 key: 'published_to_profile',
 label: 'Public page',
 sortable: true,
 render: (row) => (
 <label className="inline-flex items-center gap-2 cursor-pointer">
 <input
 type="checkbox"
 checked={!!row.published_to_profile}
 onChange={(e) => {
 e.stopPropagation();
 togglePublishOnProfile({
 id: row.id,
 published_to_profile: e.target.checked,
 });
 }}
 disabled={isTogglingPublish && togglePublishVars?.id === row.id}
 className="rounded border-gray-300 text-gray-900 focus:ring-gray-500"
 title="Show on your public vendor profile"
 />
 <span className="text-sm text-gray-600">{row.published_to_profile ? 'On' : 'Off'}</span>
 </label>
 ),
 },
 {
 key: 'is_active',
 label: 'Status',
 sortable: false,
 render: (row) => {
 const validity = getDiscountDateValidity(row);
 let label;
 let className;
 if (!row.is_active) {
 label = 'Inactive';
 className = 'bg-gray-100 text-gray-600';
 } else if (validity === 'expired') {
 label = 'Expired';
 className = 'bg-amber-100 text-amber-800';
 } else if (validity === 'not_started') {
 label = 'Scheduled';
 className = 'bg-yellow-100 text-yellow-800';
 } else {
 label = 'Active';
 className = 'bg-green-100 text-green-800';
 }
 return (
 <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${className}`}>{label}</span>
 );
 },
 },
 ],
 [copyDiscountCode, shareDiscountCode, togglePublishOnProfile, isTogglingPublish, togglePublishVars]
 );

 const tableActions = [
 {
 label: 'Edit',
 icon: Edit,
 variant: 'default',
 title: 'Edit',
 onClick: (row) => openEdit(row),
 },
 {
 label: 'Delete',
 icon: Trash2,
 variant: 'danger',
 title: 'Delete',
 onClick: (row) => {
 if (window.confirm('Delete this discount code?')) {
 deleteMutation.mutate(row.id);
 }
 },
 },
 ];

 return (
 <div className="space-y-5">
 <VendorPageHeader
 title="Discount Codes"
 subtitle='Create codes for checkout. Turn on "Public page" to show a code on your vendor listing.'
 actions={
 <Button onClick={openCreate} icon={Plus} iconPosition="left" className="rounded-md">
 Create code
 </Button>
 }
 />

 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.discountCodes.title}
 steps={VENDOR_PAGE_GUIDES.discountCodes.steps}
 icon={ListChecks}
 />

 <div className="rounded-lg border border-border bg-surface shadow-sm overflow-hidden">
 <div className="border-b border-border px-4 py-3 sm:px-5">
 <div
 className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
 role="tablist"
 aria-label="Discount code status"
 >
 {['all', 'active', 'inactive'].map((key) => {
 const isActive = activeFilter === key;
 return (
 <button
 key={key}
 type="button"
 role="tab"
 aria-selected={isActive}
 onClick={() => setActiveFilter(key)}
 className={`px-3.5 py-2 rounded-lg text-sm font-medium capitalize transition-all ${ isActive ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-gray-900' }`}
 >
 {key}
 </button>
 );
 })}
 </div>
 </div>

 <div className="p-4 sm:p-5">
 {error ? (
 <div className="rounded-xl border border-red-200 bg-red-50 text-red-600 px-4 py-3 text-sm">
 Failed to load discount codes.
 </div>
 ) : !isLoading && codes.length === 0 ? (
 <VendorEmptyState
 icon={Gift}
 title="No discount codes yet"
 description="Create a promo code clients can enter at checkout — percent or fixed amount off."
 tips={[
 'Use your business initials as a code prefix',
 'Set an expiry date so old promos stop automatically',
 'Optional: publish a code on your public profile',
 ]}
 actionLabel="Create your first code"
 onAction={openCreate}
 />
 ) : (
 <DataTable
 data={codes}
 columns={columns}
 actions={tableActions}
 loading={isLoading}
 searchable
 filterable={false}
 sortable
 selectable={false}
 onSearch={handleDiscountSearch}
 searchPlaceholder="Search codes, discount, dates, uses…"
 emptyMessage="No discount codes match your search."
 pageSize={15}
 showPagination
 className="shadow-none"
 />
 )}
 </div>
 </div>

 <ModalShell
 isOpen={modalOpen}
 onClose={closeModal}
 labelledBy="discount-code-modal-title"
 panelClassName="max-w-2xl"
 >
 <div className="bg-surface rounded-lg shadow-sm border border-border p-5 sm:p-6">
 <h2 id="discount-code-modal-title" className="text-lg font-semibold text-ink mb-4">
 {editingId ? 'Edit discount code' : 'Create discount code'}
 </h2>
 <form onSubmit={handleSubmit} className="space-y-4">
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
 <div className="flex gap-2">
 <Input
 value={form.code}
 onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
 placeholder={
 validPrefixes.length
 ? `e.g. ${validPrefixes[validPrefixes.length - 1]}-1232 (2–4 letters from business name)`
 : 'e.g. SAVE-1232'
 }
 required
 disabled={!!editingId}
 className="flex-1"
 />
 {!editingId && validPrefixes.length > 0 && (
 <Button
 type="button"
 variant="outline"
 size="sm"
 icon={RefreshCw}
 onClick={() => {
 const prefix = validPrefixes[validPrefixes.length - 1];
 const digits = String(Math.floor(1000 + Math.random() * 9000));
 setForm((f) => ({ ...f, code: `${prefix}-${digits}` }));
 }}
 title="Generate code"
 >
 Generate
 </Button>
 )}
 </div>
 {editingId && (
 <p className="text-xs text-gray-500 mt-1">Code cannot be changed when editing.</p>
 )}
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <CustomSelect
 label="Type"
 value={form.discount_type}
 onChange={(val) => setForm((f) => ({ ...f, discount_type: val }))}
 placeholder="Select type"
 options={[
 { value: PERCENTAGE, label: 'Percentage' },
 { value: FIXED, label: 'Fixed amount' },
 ]}
 size="sm"
 className="!min-h-[2.5rem]"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">
 Value * {form.discount_type === PERCENTAGE ? '(%)' : '($)'}
 </label>
 <Input
 type="number"
 min="0"
 step={form.discount_type === PERCENTAGE ? '1' : '0.01'}
 max={form.discount_type === PERCENTAGE ? '100' : undefined}
 value={form.discount_value}
 onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))}
 placeholder={form.discount_type === PERCENTAGE ? '20' : '25.00'}
 required
 size="sm"
 className="min-h-[2.5rem]"
 />
 </div>
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Min. booking amount ($)</label>
 <Input
 type="number"
 min="0"
 step="0.01"
 value={form.min_booking_amount}
 onChange={(e) => setForm((f) => ({ ...f, min_booking_amount: e.target.value }))}
 placeholder="Optional"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Max uses</label>
 <Input
 type="number"
 min="1"
 value={form.max_uses}
 onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))}
 placeholder="Unlimited"
 />
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <DatePicker
 label="Valid from"
 value={form.valid_from}
 onChange={(val) => setForm((f) => ({ ...f, valid_from: val }))}
 placeholder="dd/mm/yyyy"
 allowPastDates={true}
 />
 </div>
 <div>
 <DatePicker
 label="Valid until"
 value={form.valid_until}
 onChange={(val) => setForm((f) => ({ ...f, valid_until: val }))}
 placeholder="dd/mm/yyyy"
 allowPastDates={true}
 minDate={form.valid_from || undefined}
 />
 </div>
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
 <Input
 value={form.description}
 onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
 placeholder="e.g. Summer promotion"
 />
 </div>
 <div className="flex items-center gap-2">
 <input
 type="checkbox"
 id="is_active"
 checked={form.is_active}
 onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
 className="rounded border-gray-300 text-ink focus:ring-ink"
 />
 <label htmlFor="is_active" className="text-sm text-gray-700">Active</label>
 </div>
 <div className="flex items-start gap-2 rounded-xl border border-border p-3 bg-surface-muted">
 <input
 type="checkbox"
 id="published_to_profile"
 checked={form.published_to_profile}
 onChange={(e) =>
 setForm((f) => ({ ...f, published_to_profile: e.target.checked }))
 }
 className="rounded border-gray-300 text-ink focus:ring-ink mt-0.5"
 />
 <div>
 <label htmlFor="published_to_profile" className="text-sm font-medium text-ink">
 Show on public vendor page
 </label>
 <p className="text-xs text-ink-tertiary mt-1">
 Visitors on your listing can copy this code. Only codes that are active and still valid
 appear to clients.
 </p>
 </div>
 </div>
 <div className="flex justify-end gap-2 pt-2">
 <Button type="button" variant="outline" onClick={closeModal} className="rounded-md">
 Cancel
 </Button>
 <Button
 type="submit"
 loading={createMutation.isPending || updateMutation.isPending}
 icon={Loader2}
 disabled={createMutation.isPending || updateMutation.isPending}
 className="rounded-md"
 >
 {editingId ? 'Update' : 'Create'}
 </Button>
 </div>
 </form>
 </div>
 </ModalShell>
 </div>
 
 );
};

export default VendorDiscountCodes;
